import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Vote, ShieldCheck, Eye, Users, Info } from "lucide-react";
import { getPublicConsulta, verifyVoter, submitVote, verifyVoterPassword } from "../lib/api";
import { toast } from "sonner";
import { useAppConfig } from "../lib/AppConfigContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { LegalFooter } from "../components/LegalFooter";
import { ImageCarousel } from "../components/ImageCarousel";
import { MarkdownText } from "../components/MarkdownText";

import { VotingErrorView } from "../components/voting/VotingErrorView";
import { IdentificationStep } from "../components/voting/IdentificationStep";
import { VerificationStep } from "../components/voting/VerificationStep";
import { VoteStep } from "../components/voting/VoteStep";
import { ConfirmStep } from "../components/voting/ConfirmStep";
import { SuccessStep } from "../components/voting/SuccessStep";
import { RGPDVoterAcceptance } from "../components/voting/RGPDVoterAcceptance";
import { API_URL } from "../lib/config";

export default function VotingPage() {
  const { appName } = useAppConfig();
  const { t, i18n } = useTranslation();
  const { consultaId } = useParams();

  const [step, _setStep] = useState(1);
  const setStep = (s) => { _setStep(s); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [consulta, setConsulta] = useState(null);
  const [consultaError, setConsultaError] = useState(null);
  const [dni, setDni] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [password, setPassword] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const [voteResult, setVoteResult] = useState(null);
  const [rgpdAccepted, setRgpdAccepted] = useState(false);
  const isPreview = consulta?.preview_mode === true;

  const birthDate = birthDay && birthMonth && birthYear
    ? `${birthDay.padStart(2, "0")}/${birthMonth.padStart(2, "0")}/${birthYear}`
    : "";

  const isMultipleSelection = consulta && (
    (consulta.restriction_type === "options" && consulta.max_options > 1) ||
    consulta.restriction_type === "amount"
  );

  const calculateSelectedAmount = () => {
    if (!consulta?.options) return 0;
    return selectedOptions.reduce((total, optTitle) => {
      const opt = consulta.options.find(o => (typeof o === "string" ? o : o.title) === optTitle);
      return (opt && typeof opt === "object" && opt.amount) ? total + opt.amount : total;
    }, 0);
  };

  const toggleOption = (optionTitle) => {
    const optionData = consulta.options.find(o => (typeof o === "string" ? o : o.title) === optionTitle);
    const optionAmount = typeof optionData === "object" ? optionData.amount : 0;

    if (selectedOptions.includes(optionTitle)) {
      setSelectedOptions(selectedOptions.filter(o => o !== optionTitle));
    } else {
      if (consulta.restriction_type === "options" && selectedOptions.length >= consulta.max_options) {
        toast.error(t('voting.maxOptionsReached', { max: consulta.max_options }));
        return;
      }
      if (consulta.restriction_type === "amount" && consulta.max_total_amount) {
        const newTotal = calculateSelectedAmount() + (optionAmount || 0);
        if (newTotal > consulta.max_total_amount) {
          toast.error(t('voting.maxAmountExceeded', { amount: consulta.max_total_amount.toLocaleString(undefined, { style: "currency", currency: "EUR" }) }));
          return;
        }
      }
      setSelectedOptions([...selectedOptions, optionTitle]);
    }
  };

  useEffect(() => { loadConsulta(); }, [consultaId]);

  const loadConsulta = async () => {
    try {
      const response = await getPublicConsulta(consultaId);
      setConsulta(response.data);
      if (response.data.entity_language) {
        i18n.changeLanguage(response.data.entity_language);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        setConsultaError(detail);
        if (detail.entity_language) i18n.changeLanguage(detail.entity_language);
      }
      else if (detail && typeof detail === "string") setConsultaError({ message: detail });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDni = async (e) => {
    e.preventDefault();
    if (!dni.trim()) { toast.error(t("voting.enterDni")); return; }
    if (!birthDate) { toast.error(t("voting.enterBirthDate")); return; }

    setSubmitting(true);
    try {
      const response = await verifyVoter(dni, consultaId, birthDate);
      if (response.data.already_voted) { toast.error(t("voting.alreadyVoted")); return; }
      setNeedsPassword(response.data.needs_password);
      setRemainingAttempts(response.data.remaining_attempts);
      setStep(response.data.needs_password ? 2 : 3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "DNI o data de naixement incorrectes");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyIdentity = async (e) => {
    e.preventDefault();
    if (needsPassword && !password) { toast.error(t("voting.enterPassword")); return; }

    if (needsPassword) {
      setSubmitting(true);
      try {
        await verifyVoterPassword(consultaId, dni, birthDate, password);
        setStep(3);
      } catch (error) {
        const detail = error.response?.data?.detail || t("errors.wrongPassword");
        toast.error(detail);
        setPassword("");
        if (detail.includes("intents")) {
          const match = detail.match(/(\d+) intents/);
          if (match) setRemainingAttempts(parseInt(match[1]));
        }
        if (detail.includes("superat") || detail.includes("maxim")) setStep(1);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStep(3);
  };

  const handleSubmitVote = async () => {
    if (isMultipleSelection) {
      if (selectedOptions.length === 0) { toast.error(t("voting.selectAtLeastOne")); return; }
      if (consulta.restriction_type === "options") {
        if (selectedOptions.length < consulta.min_options) { toast.error(`Has de seleccionar minim ${consulta.min_options} opcions`); return; }
        if (selectedOptions.length > consulta.max_options) { toast.error(`Maxim ${consulta.max_options} opcions permeses`); return; }
      } else if (consulta.restriction_type === "amount" && consulta.max_total_amount && calculateSelectedAmount() > consulta.max_total_amount) {
        toast.error("L'import total supera el maxim permes"); return;
      }
    } else if (!selectedOption) {
      toast.error(t("voting.selectAnOption")); return;
    }
    // Go to confirmation step
    setStep(4);
  };

  const handleConfirmAndSend = async () => {
    // Preview mode: show mock success
    if (isPreview) {
      setVoteResult({
        message: t("voting.voteRegistered"),
        vote_hash: "PREVIEW-" + Math.random().toString(36).substring(2, 10).toUpperCase(),
        blockchain_tx: null,
        preview_mode: true,
      });
      setStep(5);
      return;
    }

    setSubmitting(true);
    try {
      const realOption = selectedOption.includes(":::") ? selectedOption.split(":::")[0] : selectedOption;
      const voteValue = isMultipleSelection ? selectedOptions.join(";;;") : realOption;
      const data = { consulta_id: consultaId, dni, birth_date: birthDate, vote: voteValue };
      if (needsPassword) data.password = password;
      const response = await submitVote(data);
      setVoteResult(response.data);
      setStep(5);
      toast.success(t("voting.voteRegistered"));
    } catch (error) {
      const detail = error.response?.data?.detail || t("errors.votingError");
      toast.error(detail);
      if (detail.includes("intents")) {
        const match = detail.match(/(\d+) intents/);
        if (match) setRemainingAttempts(parseInt(match[1]));
        if (needsPassword && (detail.includes("Contrasenya") || detail.includes("intents"))) {
          setPassword("");
          setStep(2);
        }
      }
      if (detail.includes("superat") || detail.includes("5 errors") || detail.includes("maxim")) setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(1); setDni(""); setBirthDay(""); setBirthMonth(""); setBirthYear("");
    setPassword(""); setVoteResult(null); setSelectedOption(""); setSelectedOptions([]);
    setNeedsPassword(false); setRgpdAccepted(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="spinner" /></div>;
  }

  if (!consulta) {
    return <VotingErrorView error={consultaError} />;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="voting-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          {consulta.entity_logo_url ? (
            <img
              src={`${API_URL}${consulta.entity_logo_url}`}
              alt={consulta.entity_name || appName}
              className="w-10 h-10 rounded-lg object-cover"
              data-testid="entity-logo"
            />
          ) : (
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Vote className="h-5 w-5 text-white" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="font-heading font-bold text-slate-900" data-testid="entity-name">
              {consulta.entity_name || appName}
            </h1>
            <p className="text-xs text-slate-500">{t("voting2.votingSystem")}</p>
          </div>
          <LanguageSelector />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Preview Banner */}
        {isPreview && (
          <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="preview-banner">
            <Eye className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 font-medium">{t("voting2.previewMode")}</p>
          </div>
        )}
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-12">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
                step > s ? "bg-emerald-500 text-white" : step === s ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step > s ? "\u2713" : s}
              </div>
              {s < 5 && <div className={`w-6 sm:w-12 h-1 mx-1 sm:mx-2 ${step > s ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {/* Consulta Info */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 rounded-xl p-6 mb-8">
          {(consulta.image_urls?.length || consulta.image_url) && (
            <ImageCarousel
              images={(consulta.image_urls?.length ? consulta.image_urls : (consulta.image_url ? [consulta.image_url] : [])).map(u => `${API_URL}${u}`)}
              alt={consulta.title}
              maxHeight="max-h-80"
              className="rounded-lg mb-4"
            />
          )}
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">{consulta.title}</h2>
          <MarkdownText className="text-slate-600">{consulta.description}</MarkdownText>
        </motion.div>

        {/* Global Privacy Info Panel */}
        {step === 1 && !rgpdAccepted && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl"
            data-testid="global-privacy-panel"
          >
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-slate-800 text-sm">{t('votingMode.global.title')}</p>
                <div className="mt-2 flex flex-col gap-2 text-sm">
                  {consulta?.enable_vot_ma_alcada ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 flex-shrink-0 text-orange-600" />
                        <span className="text-orange-700 font-medium">{t('votingMode.global.type2')}</span>
                      </div>
                      <p className="text-slate-500 text-xs ml-6 mt-0.5">{t('votingMode.global.type2Desc')}</p>
                    </div>
                  ) : consulta?.enable_voter_tracking ? (
                    <>
                      <div>
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                          <span className="text-emerald-700 font-medium">{t('votingMode.global.type1')}</span>
                        </div>
                        <p className="text-slate-500 text-xs ml-6 mt-0.5">{t('votingMode.global.type1Desc')}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 flex-shrink-0 text-amber-600" />
                          <span className="text-amber-700 font-medium">{t('votingMode.global.type3')}</span>
                        </div>
                        <p className="text-slate-500 text-xs ml-6 mt-0.5">{t('votingMode.global.type3Desc')}</p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                        <span className="text-emerald-700 font-medium">{t('votingMode.global.type1')}</span>
                      </div>
                      <p className="text-slate-500 text-xs ml-6 mt-0.5">{t('votingMode.global.type1Desc')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* RGPD acceptance step - anonymous, tracking, or mà alçada */}
        {step === 1 && !rgpdAccepted && (
          <RGPDVoterAcceptance
            entityName={consulta?.entity_name}
            entityEmail={consulta?.entity_email}
            onAccepted={() => {
              setRgpdAccepted(true);
              if (isPreview) setStep(3);
            }}
            variant={consulta?.enable_vot_ma_alcada ? "maAlcada" : consulta?.enable_voter_tracking ? "tracking" : "anonymous"}
          />
        )}

        {step === 1 && rgpdAccepted && !isPreview && (
          <IdentificationStep
            dni={dni} setDni={setDni}
            birthDay={birthDay} setBirthDay={setBirthDay}
            birthMonth={birthMonth} setBirthMonth={setBirthMonth}
            birthYear={birthYear} setBirthYear={setBirthYear}
            submitting={submitting} onSubmit={handleVerifyDni}
            enableVoterTracking={consulta?.enable_voter_tracking}
            enableVotMaAlcada={consulta?.enable_vot_ma_alcada}
            enableAudit={consulta?.enable_audit}
          />
        )}

        {step === 2 && !isPreview && (
          <VerificationStep
            needsPassword={needsPassword}
            password={password} setPassword={setPassword}
            birthDay={birthDay} setBirthDay={setBirthDay}
            birthMonth={birthMonth} setBirthMonth={setBirthMonth}
            birthYear={birthYear} setBirthYear={setBirthYear}
            remainingAttempts={remainingAttempts}
            submitting={submitting} onSubmit={handleVerifyIdentity}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && (
          <VoteStep
            consulta={consulta} isMultipleSelection={isMultipleSelection}
            selectedOption={selectedOption} setSelectedOption={setSelectedOption}
            selectedOptions={selectedOptions} toggleOption={toggleOption}
            calculateSelectedAmount={calculateSelectedAmount}
            submitting={submitting} onSubmit={handleSubmitVote}
            onBack={() => setStep(isPreview ? 1 : 2)}
          />
        )}

        {step === 4 && (
          <ConfirmStep
            consulta={consulta}
            selectedOption={selectedOption}
            selectedOptions={selectedOptions}
            isMultipleSelection={isMultipleSelection}
            onConfirm={handleConfirmAndSend}
            onGoBack={() => setStep(3)}
            submitting={submitting}
          />
        )}

        {step === 5 && voteResult && (
          <SuccessStep voteResult={voteResult} onReset={handleReset} isPreview={isPreview} />
        )}
      </main>
      <div className="max-w-2xl mx-auto px-4 pb-8">
        <LegalFooter
          enableVotMaAlcada={consulta?.enable_vot_ma_alcada}
          enableAudit={consulta?.enable_audit}
          enableVoterTracking={consulta?.enable_voter_tracking}
        />
      </div>
    </div>
  );
}
